/**
 * PersonaContext — Types, constants, and derivation logic for persona-adaptive UX.
 * Story 13.6: Persona-Adaptive Density System
 *
 * Pure functions only — no React dependencies. Consumed by persona-provider.tsx.
 */

/**
 * Persona archetypes derived from RBAC roles.
 * Maps to the 6 named personas in the UX spec.
 */
type PersonaArchetype =
  | "admin" // Platform Admin — Marcus
  | "reviewer" // HITL Reviewer — David
  | "architect" // Cloud Architect — Sarah
  | "steward" // Data Steward — Diane
  | "manager" // Engagement Manager — Rachel
  | "auditor" // Security Auditor — James
  | "data-workflow"; // Data Engineer — generic

/**
 * Density modes controlling spacing, row heights, and card padding.
 */
type DensityMode = "compact" | "default" | "comfortable";

/**
 * Side nav context sections — drives which nav items are visible.
 */
type SideNavContext =
  | "home"
  | "pipeline"
  | "admin"
  | "review"
  | "governance"
  | "ops"
  | "audit"
  | "agent"
  | "profiler";

/**
 * Full persona context — drives density, navigation, and behavior defaults.
 */
interface PersonaContext {
  /** Active persona archetype */
  persona: PersonaArchetype;
  /** Computed density (persona default or user override) */
  density: DensityMode;
  /** Landing route for this persona */
  landingRoute: string;
  /** Current side nav context (derived from route) */
  sideNavContext: SideNavContext;
  /** Whether auto-advance is ON by default for this persona */
  autoAdvanceDefault: boolean;
  /** Whether the current density is a user override (not persona default) */
  isUserOverride: boolean;
}

/**
 * Role-to-persona mapping.
 * RBAC roles map to persona archetypes which drive UX defaults.
 */
const ROLE_TO_PERSONA: Record<string, PersonaArchetype> = {
  "platform-admin": "admin",
  "data-engineer": "data-workflow",
  "hitl-reviewer": "reviewer",
  "engagement-manager": "manager",
  "security-auditor": "auditor",
  "data-steward": "steward",
};

/**
 * Persona-to-density default mapping.
 */
const PERSONA_DENSITY_DEFAULTS: Record<PersonaArchetype, DensityMode> = {
  reviewer: "compact", // David — high-density evidence display
  admin: "compact", // Marcus — dense ops monitoring
  auditor: "compact", // James — dense compliance data
  steward: "default", // Diane — balanced governance view
  architect: "default", // Sarah — balanced deployment config
  manager: "comfortable", // Rachel — spacious summary cards
  "data-workflow": "default",
};

/**
 * Persona-to-landing-route mapping.
 */
const PERSONA_LANDING_ROUTES: Record<PersonaArchetype, string> = {
  reviewer: "/reviews",
  admin: "/ops",
  auditor: "/audit",
  steward: "/governance",
  architect: "/deploy",
  manager: "/reports",
  "data-workflow": "/pipelines",
};

/**
 * Persona-to-auto-advance-default mapping.
 * Operational roles default to ON; governance roles default to OFF.
 */
const PERSONA_AUTO_ADVANCE: Record<PersonaArchetype, boolean> = {
  reviewer: true, // David — high throughput
  admin: true, // Marcus — fast triage
  auditor: false, // James — deliberate review
  steward: false, // Diane — careful governance
  architect: false, // Sarah — no review queue
  manager: false, // Rachel — no review queue
  "data-workflow": false,
};

/**
 * Route-to-side-nav-context mapping.
 */
function deriveSideNavContext(pathname: string): SideNavContext {
  if (pathname.startsWith("/profiler")) return "profiler";
  if (pathname.startsWith("/pipelines")) return "pipeline";
  if (pathname.startsWith("/reviews")) return "review";
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/governance")) return "governance";
  if (pathname.startsWith("/ops")) return "ops";
  if (pathname.startsWith("/audit")) return "audit";
  if (pathname.startsWith("/agents")) return "agent";
  return "home";
}

/**
 * Derive full persona context from user role, route, and user preferences.
 */
function derivePersonaContext(
  userRole: string,
  pathname: string,
  densityOverride: DensityMode | null,
): PersonaContext {
  const persona = ROLE_TO_PERSONA[userRole] ?? "data-workflow";
  const defaultDensity = PERSONA_DENSITY_DEFAULTS[persona];
  const density = densityOverride ?? defaultDensity;

  return {
    persona,
    density,
    landingRoute: PERSONA_LANDING_ROUTES[persona],
    sideNavContext: deriveSideNavContext(pathname),
    autoAdvanceDefault: PERSONA_AUTO_ADVANCE[persona],
    isUserOverride: densityOverride !== null,
  };
}

export {
  derivePersonaContext,
  deriveSideNavContext,
  ROLE_TO_PERSONA,
  PERSONA_DENSITY_DEFAULTS,
  PERSONA_LANDING_ROUTES,
  PERSONA_AUTO_ADVANCE,
  type PersonaContext,
  type PersonaArchetype,
  type DensityMode,
  type SideNavContext,
};
